import type { IndexProduct } from '@/domains/products/types/models/IndexProduct.types.ts'
import type { ProductModel, TempProductModel } from '@/domains/products/types/models/ProductModel.types.ts'
import type { FetchClientOptionsPayload } from '@/types/common/models/FetchClientOptionsPayload.ts'

interface IndexProductRequest extends FetchClientOptionsPayload {
  queryParams: {
    q?: string
    page?: number
    per_page?: number
    with?: string
    include?: string
    // Only the products filed in this taxon itself, not in its sub-categories.
    direct_taxon_id?: number
    // One id, or several joined by commas.
    id?: string
  }
}

interface GetProductRequest {
  slug: string
}

interface FindProductRequest {
  id: string
}

const FIELDS_TO_ENHANCE = {
  TITLE: 'title',
  DESCRIPTION: 'description',
  BRAND: 'brand',
  VARIANT_TYPE_OPTIONS: 'variants.*.variant_type_options',
  SKU_CODES: 'variants.*.sku_codes'
} as const

type FieldToEnhanceKeys = keyof typeof FIELDS_TO_ENHANCE
type FieldToEnhanceValues = (typeof FIELDS_TO_ENHANCE)[FieldToEnhanceKeys]

const getFieldToEnhance = (requestedNames?: FieldToEnhanceKeys[]): string[] => {
  const fieldMappingObject = FIELDS_TO_ENHANCE
  if (!requestedNames || requestedNames.length === 0) {
    return Object.values(fieldMappingObject)
  }

  const paths: string[] = []
  for (const name of requestedNames) {
    paths.push(fieldMappingObject[name])
  }
  return paths
}

interface AssociateBarcodesToProduct {
  product: IndexProduct
  barcodes: string[]
}

interface ProductEnhanceRequest {
  barcodes?: string[]
  product?: ProductModel
  fields_to_enhance: string[]
  with_lookup: boolean
}

interface UnrelatedProduct {
  barcode?: string | null
  title?: string | null
  explanation?: string | null
}

interface ProductEnhanceResponse {
  product: TempProductModel
  unrelated_products: UnrelatedProduct[]
  failed_barcodes: string[]
}

export { getFieldToEnhance, FIELDS_TO_ENHANCE }
export type {
  IndexProductRequest,
  ProductEnhanceRequest,
  ProductEnhanceResponse,
  UnrelatedProduct,
  FieldToEnhanceValues,
  AssociateBarcodesToProduct,
  GetProductRequest,
  FindProductRequest
}
